import { useEffect, useState, useContext } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Box from './Box';
import LinkItem from '../components/LinkItem';
import EmptyState from '../components/EmptyState';
import UnifiedModal from '../components/UnifiedModal';
import { Context } from '../../../AuthContext';
import { useCategory } from '../../../CategoryContext';
import { faviconUrl } from '../../../utils/functions';
import '../Workbench.css';

const Links = ({ isVertical = false, verticalId }) => {
  const { user } = useContext(Context);
  const { verticals } = useCategory();
  const [links, setLinks] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLinks = async () => {
      if (!user?.uid) return;
      setIsLoading(true);
      try {
        const token = await user.getIdToken();
        const url =
          process.env.REACT_APP_FLASK_WEBSERVER +
          'get_links' +
          (isVertical && verticalId ? `?vertical_id=${encodeURIComponent(verticalId)}` : '');
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setLinks(data.data);
      } catch (error) {
        console.error('Error fetching links:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLinks();
  }, [user?.uid, isVertical, verticalId]);

  const handleDelete = async (id) => {
    setLinks(links.filter((el) => el._id !== id));
    try {
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}delete_link/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.log('Failed to delete link:', error);
    }
  };

  const handleOpenModal = (item = null) => {
    setEditItem(item);
    setOpenModal(true);
  };

  const handleSave = (updatedItem) => {
    setLinks((prev) =>
      editItem
        ? prev.map((item) => (item._id === editItem._id ? updatedItem : item))
        : [...prev, updatedItem]
    );
    setOpenModal(false);
  };

  const handleDragEnd = async (result) => {
    if (!canManage) return;
    if (!result.destination) return;

    const updated = Array.from(links);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);

    setLinks(updated);

    try {
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}update_order_dashboard?collection=links`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updated.map((item) => ({ id: item._id }))),
        }
      );
    } catch (error) {
      console.error('Failed to update links order:', error);
    }
  };

  // Owner/editor-only management rights. The personal workbench is always the
  // user's own, so it's always allowed there; the vertical workbench requires
  // edit rights. The per-item menu (the only way to edit/delete) is hidden from
  // Members.
  const canManage =
    !isVertical ||
    (verticals || []).some(
      (v) =>
        v.id === verticalId &&
        (v.status === 'Created' || /^(editor|advancededitor)$/i.test(v.permission || ''))
    );

  return (
    <div className="workbench-tasks">
      <UnifiedModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        item={editItem}
        type="link"
        onSave={handleSave}
        isVertical={isVertical}
        verticalId={verticalId}
      />

      <Box
        count={links.length}
        onAddHandler={() => handleOpenModal()}
        title="Links"
        icon="link.svg"
        isLoading={isLoading}
        canAdd={canManage}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="links">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} style={{ height: '100%' }}>
                {links.length > 0 ? (
                  links.map((link, index) => (
                    <Draggable
                      key={link._id}
                      draggableId={link._id}
                      index={index}
                      isDragDisabled={!canManage}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <LinkItem
                            id={link._id}
                            title={link.title}
                            link={link.link}
                            icon={faviconUrl(link.link)}
                            deleteHandler={() => handleDelete(link._id)}
                            editHandler={() => handleOpenModal(link)}
                            canEdit={canManage}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))
                ) : (
                  <EmptyState
                    imageSrc="/link.svg"
                    type="link"
                    onAddClick={canManage ? () => handleOpenModal() : undefined}
                  />
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </div>
  );
};

export default Links;
